import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useNotification } from '../../shared/contexts/NotificationContext';
import type { CartItem } from '../../shared/types';

function Carrinho() {
  const navigate = useNavigate();
  const { error: showError } = useNotification();
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [subtotal, setSubtotal] = useState(0);
  const [shippingCost, setShippingCost] = useState(0);

  useEffect(() => {
    loadCart();
  }, []);

  useEffect(() => {
    calculateTotals();
  }, [cartItems]);

  const loadCart = async () => {
    setLoading(true);
    try {
      // TODO: Implementar carregamento do carrinho da API
      // Por enquanto, usar localStorage ou estado vazio
      const savedCart = localStorage.getItem('cart');
      if (savedCart) {
        setCartItems(JSON.parse(savedCart));
      } else {
        setCartItems([]);
      }
    } catch (error) {
      console.error('Erro ao carregar carrinho:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateTotals = () => {
    const sub = cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);
    setSubtotal(sub);
    // Frete grátis para compras acima de R$ 200
    setShippingCost(sub >= 200 ? 0 : 15);
  };

  const updateQuantity = (itemId: number, newQuantity: number) => {
    if (newQuantity < 1) {
      removeItem(itemId);
      return;
    }
    const updated = cartItems.map(item =>
      item.id === itemId ? { ...item, quantity: newQuantity } : item
    );
    setCartItems(updated);
    localStorage.setItem('cart', JSON.stringify(updated));
  };

  const removeItem = (itemId: number) => {
    const updated = cartItems.filter(item => item.id !== itemId);
    setCartItems(updated);
    localStorage.setItem('cart', JSON.stringify(updated));
  };

  const handleCheckout = () => {
    if (cartItems.length === 0) {
      showError('Seu carrinho está vazio');
      return;
    }
    navigate('/checkout');
  };

  const formatPrice = (price: number) => {
    return `R$ ${price.toFixed(2).replace('.', ',')}`;
  };

  const total = subtotal + shippingCost;

  if (loading) {
    return (
      <div className="mobile-content">
        <div className="loading-message">Carregando carrinho...</div>
      </div>
    );
  }

  return (
    <div className="mobile-content">
      {/* Cart Items */}
      <section className="cart-items">
        {cartItems.length === 0 ? (
          <div className="empty-message">
            <p>Seu carrinho está vazio</p>
            <Link to="/catalogo" className="btn btn-primary">Ver Produtos</Link>
          </div>
        ) : (
          cartItems.map((item) => (
            <div key={item.id} className="cart-item">
              <img
                src={item.image || `data:image/svg+xml,%3Csvg width='100' height='100' xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='100' height='100' fill='%23f0f0f0'/%3E%3Ctext x='50%25' y='50%25' text-anchor='middle' dominant-baseline='middle' font-family='Arial, sans-serif' font-size='11' fill='%23999'%3EItem%3C/text%3E%3C/svg%3E`}
                alt={item.name}
                className="cart-item-image"
              />
              <div className="cart-item-info">
                <h3 className="cart-item-name">{item.name}</h3>
                <p className="cart-item-details">
                  {item.color && `Cor: ${item.color}`}
                  {item.color && item.size && ' | '}
                  {item.size && `Tamanho: ${item.size}`}
                </p>
                <div className="cart-item-price">{formatPrice(item.price)}</div>
              </div>
              <div className="cart-item-actions">
                <div className="quantity-controls">
                  <button
                    type="button"
                    className="qty-btn"
                    onClick={() => updateQuantity(item.id, item.quantity - 1)}
                  >
                    -
                  </button>
                  <span className="qty-value">{item.quantity}</span>
                  <button
                    type="button"
                    className="qty-btn"
                    onClick={() => updateQuantity(item.id, item.quantity + 1)}
                  >
                    +
                  </button>
                </div>
                <button
                  type="button"
                  className="remove-btn"
                  onClick={() => removeItem(item.id)}
                >
                  <i className="fas fa-trash"></i>
                </button>
              </div>
            </div>
          ))
        )}
      </section>

      {/* Cart Summary */}
      {cartItems.length > 0 && (
        <section className="cart-summary">
          <div className="summary-row">
            <span className="summary-label">Subtotal</span>
            <span className="summary-value">{formatPrice(subtotal)}</span>
          </div>
          <div className="summary-row">
            <span className="summary-label">Frete</span>
            <span className="summary-value">
              {shippingCost === 0 ? 'Grátis' : formatPrice(shippingCost)}
            </span>
          </div>
          <div className="summary-total">
            <span className="summary-label">Total</span>
            <span className="summary-value">{formatPrice(total)}</span>
          </div>
          <button className="checkout-btn" onClick={handleCheckout}>
            Finalizar Pedido
            <i className="fas fa-arrow-right"></i>
          </button>
        </section>
      )}
    </div>
  );
}

export default Carrinho;

